"use client";

/**
 * The backend's sign-up mode (Ticket 030), read once per page load and shared
 * by every caller: the early-access buttons, the sign-up page, the login
 * page's footer.
 *
 * - `waitlist` (SCO today) and `legacy`: early access only. Every "Get your
 *   API key" button opens the early-access form; there is no sign-up page.
 * - `open` (launch): those buttons go to `/dashboard/signup` instead.
 * - `unsupported`: the endpoint answered 404, so this is a backend without
 *   Ticket 030 (GCP until it gets it). Today's behaviour holds: no
 *   forgot-password link. Any other failure is not this: see below.
 *
 * Flipping SCO to launch is one backend setting, and because the UI follows
 * this endpoint it flips with it, with no second release.
 */

import { useEffect, useState } from "react";
import { getSignupMode } from "./client";
import type { SignupMode } from "./types";

let pending: Promise<SignupMode> | null = null;

/**
 * The mode, once per page load. `request()` already retries a failed GET
 * twice with backoff (a 429 or 5xx); if it still fails, the page behaves as
 * `waitlist` (early access goes to the backend, the password links stay) and
 * the failure is **not** kept, so the next call asks again. Only a 404
 * (`unsupported`) or a real mode is kept for the page load.
 */
export function loadSignupMode(): Promise<SignupMode> {
  pending ??= (async () => {
    const mode = await getSignupMode().catch(() => null);
    if (mode) return mode;
    pending = null;
    return "waitlist" as const;
  })();
  return pending;
}

/**
 * Forgets the cached mode, for when the backend has just shown it changed
 * (a `signup_unavailable` on register): the next call asks again.
 */
export function resetSignupMode(): void {
  pending = null;
}

/** Null until the answer arrives. */
export function useSignupMode(): SignupMode | null {
  const [mode, setMode] = useState<SignupMode | null>(null);
  useEffect(() => {
    let alive = true;
    void loadSignupMode().then((m) => {
      if (alive) setMode(m);
    });
    return () => {
      alive = false;
    };
  }, []);
  return mode;
}

/** Whether this backend has the Ticket 030 flows (activate, reset) at all. */
export function hasAccountFlows(mode: SignupMode | null): boolean {
  return mode === "waitlist" || mode === "open" || mode === "legacy";
}
